import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageUploader from '../../components/ImageUploader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { recognizeMenuFromImages } from '../../services/imageRecognition';
import testData from '../../result.json';

function HomePage() {
  const navigate = useNavigate();
  const [lastTranslation, setLastTranslation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pageTransition, setPageTransition] = useState(false);

  // 从本地存储加载上次的翻译记录
  useEffect(() => {
    const savedTranslation = localStorage.getItem('lastTranslation');
    if (savedTranslation) {
      setLastTranslation(JSON.parse(savedTranslation));
    }
  }, []);

  const handleImagesUploaded = async (images) => {
    setIsLoading(true);
    let isFirstResponse = true;
    try {
      const result = await recognizeMenuFromImages(images, (intermediateResult) => {
        if (intermediateResult?.menu) {
          // 第一次收到数据时就跳转到菜单页面
          if (isFirstResponse) {
            localStorage.setItem('lastTranslation', JSON.stringify(intermediateResult));
            navigate('/menu', { 
              state: { 
                menuData: intermediateResult,
                isStreaming: true // 标记这是流式数据
              } 
            });
            isFirstResponse = false;
            setIsLoading(false);
          }
        }
      });

      if (result) {
        // 最终结果处理
        localStorage.setItem('lastTranslation', JSON.stringify(result));
      } else {
        throw new Error('识别失败，请重试');
      }
    } catch (error) {
      console.error('Menu recognition error:', error);
      setErrorMessage('识别失败，请重试');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      setIsLoading(false);
    }
  };

  const handleLoadLastTranslation = () => {
    if (lastTranslation) {
      setPageTransition(true);
      setTimeout(() => {
        navigate('/menu', { 
          state: { 
            menuData: lastTranslation,
            isStreaming: false // 标记这不是流式数据
          } 
        });
        setPageTransition(false);
      }, 300);
    }
  };

  const handleTestData = () => {
    try {
      if (!testData || !testData.menu || !testData.menu.categories) {
        throw new Error('测试数据格式不正确');
      }
      setPageTransition(true);
      setTimeout(() => {
        navigate('/menu', { 
          state: { 
            menuData: testData,
            isStreaming: false // 标记这不是流式数据
          } 
        });
        setPageTransition(false);
      }, 300);
    } catch (error) {
      console.error('加载测试数据失败:', error);
      setErrorMessage('加载测试数据失败：' + error.message);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen">
      <LoadingOverlay isVisible={isLoading} />
      <div className="max-w-4xl mx-auto py-2 flex flex-col items-center" style={{paddingBottom: '400px'}}>
        <div className="text-center mb-2 sticky top-0 w-full z-10 bg-white p-4 sm:p-8 relative overflow-hidden">
          <h1 className="text-xl sm:text-5xl font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-500">
            靠谱-Ai菜单翻译
          </h1>
          <p className="text-gray-400 text-sm sm:text-lg max-w-md mx-auto px-2">
            拍照即刻翻译，轻松点餐无障碍
          </p>
        </div>
        <div className='px-4'>
          <div className="w-full max-w-md backdrop-blur-lg bg-white/70 rounded-2xl shadow-xl p-4 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-400 rounded-full opacity-10"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-indigo-500 rounded-full opacity-10"></div>
            
            <div className="relative z-10">
              <ImageUploader 
                onImagesUploaded={handleImagesUploaded}
                onError={(message) => {
                  setErrorMessage(message);
                  setShowError(true);
                  setTimeout(() => {
                    setShowError(false);
                    setErrorMessage('');
                  }, 3000);
                }}
              />

              {/* 功能按钮区域 */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* 上次翻译记录按钮 */}
                {lastTranslation && (
                  <button
                    onClick={handleLoadLastTranslation}
                    className="backdrop-blur-sm bg-white/60 border border-gray-200/60 px-4 py-2 rounded-xl text-gray-700 hover:bg-white/70 hover:border-indigo-300/70 transition-all duration-300 shadow-sm flex flex-row items-center justify-between group"
                    disabled={pageTransition}
                  >
                    {pageTransition ? (
                      <div className="flex items-center justify-center text-indigo-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 mr-2"></div>
                        <span>加载中...</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-6 h-6 text-indigo-500 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm">查看上次翻译</span>
                      </>
                    )}
                  </button>
                )}
                
                {/* 测试数据按钮 */}
                <button
                  onClick={handleTestData}
                  className="backdrop-blur-sm bg-indigo-600/80 text-white px-4 py-2 rounded-xl hover:bg-indigo-700/80 transition-all duration-300 shadow-md hover:shadow-lg flex flex-row items-center justify-between group h-full"
                  disabled={pageTransition}
                >
                  {pageTransition ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      <span>加载中...</span>
                    </div>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">体验演示菜单</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 常用短语区域 */}
          <div className="w-full max-w-md mt-6">
            <div
              onClick={() => navigate('/phrase')}
              className="backdrop-blur-lg bg-white/70 rounded-2xl shadow-xl p-4 sm:p-6 relative overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-400 rounded-full opacity-10 group-hover:scale-110 transition-transform duration-300"></div>
              <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-pink-500 rounded-full opacity-10 group-hover:scale-110 transition-transform duration-300"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 group-hover:text-purple-600 transition-colors">常用短语</h2>
                    <p className="text-sm text-gray-600 mt-1">点餐必备的常用对话短语</p>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl text-white shadow-lg transform group-hover:scale-110 transition-all duration-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                </div>
                
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">点餐用语</span>
                  <span className="px-3 py-1 bg-pink-100 text-pink-600 rounded-full text-sm">特殊要求</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">支付用语</span>
                </div>
              </div>
            </div>
          </div>

          {/* 汇率助手区域 */}
          <div className="w-full max-w-md mt-6">
            <div
              onClick={() => navigate('/exchange')}
              className="backdrop-blur-lg bg-white/70 rounded-2xl shadow-xl p-4 sm:p-6 relative overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-400 rounded-full opacity-10 group-hover:scale-110 transition-transform duration-300"></div>
              <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-teal-500 rounded-full opacity-10 group-hover:scale-110 transition-transform duration-300"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 group-hover:text-green-600 transition-colors">汇率助手</h2>
                    <p className="text-sm text-gray-600 mt-1">快速进行货币汇率换算</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-teal-500 p-3 rounded-xl text-white shadow-lg transform group-hover:scale-110 transition-all duration-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm">数十种货币</span>
                  <span className="px-3 py-1 bg-teal-100 text-teal-600 rounded-full text-sm">离线支持</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {showError && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg">
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export default HomePage; 